import React, { useRef, useState } from "react";
import classNames from "classnames";
import { Label } from "react-aria-components";
import { Label as SemanticLabel } from "semantic-ui-react";
import { Dropdown } from "semantic-ui-react";
import { StringCondition } from "../../queries/getCrmRequests";
import { IBaseControlProps, nestedErrorMessageSelector } from "./Utils";
import { Controller } from "react-hook-form";
import "./StringFilter.scss";

const StringOperatorOptions: {
  key: StringCondition;
  text: string;
  value: StringCondition;
}[] = [
  { key: "Equal", text: "Is Equal To", value: "Equal" },
  { key: "NotEqual", text: "Is Not Equal To", value: "NotEqual" },
  { key: "Contains", text: "Contains", value: "Contains" },
  { key: "NotContains", text: "Does Not Contain", value: "NotContains" },
  { key: "StartsWith", text: "Starts With", value: "StartsWith" },
  { key: "NotStartsWith", text: "Does Not Start With", value: "NotStartsWith" },
  { key: "Empty", text: "Is Empty", value: "Empty" },
  { key: "NotEmpty", text: "Is Not Empty", value: "NotEmpty" },
];
interface IStringFilterProps {
  label: string;
  defaultValues?: string[];
  defaultOperator?: StringCondition;
  className?: string;
  useEmptyOperator?: boolean;
  onChange?: (values: string[]) => void;
  onOperatorChange?: (operator: StringCondition, values: string[]) => void;
}

export const StringFilter = ({
  label,
  defaultValues = [],
  defaultOperator = "Equal",
  className,
  useEmptyOperator = true,
  onChange,
  onOperatorChange,
}: IStringFilterProps) => {
  const [values, setValues] = useState<string[]>(defaultValues);
  const [operator, setOperator] = useState<StringCondition>(defaultOperator);
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldRenderInput = operator !== "Empty" && operator !== "NotEmpty";

  return (
    <div className={className}>
      <div className='tw-flex tw-w-full tw-items-center'>
        <Label className='tw-font-bold'>{label}</Label>
        <Dropdown
          id='String-range-operator-dropdown'
          className='tw-ml-4 tw-font-bold tw-text-blue'
          options={
            useEmptyOperator
              ? StringOperatorOptions
              : StringOperatorOptions.filter((option) => option.value !== "Empty" && option.value !== "NotEmpty")
          }
          defaultValue={operator}
          onChange={(_, { value }) => {
            setOperator(value as StringCondition);
            if (onOperatorChange) {
              if (value === "Empty" || value === "NotEmpty") {
                onOperatorChange(value as StringCondition, []);
                return;
              }
              const currentValue = inputRef.current?.value;
              const valuesToUse = currentValue ? [...values, currentValue.trim()] : values;
              onOperatorChange(value as StringCondition, valuesToUse);
            }
          }}
        />
      </div>
      {shouldRenderInput && (
        <div className='tw-mt-2'>
          <MultiStringInput inputRef={inputRef} values={values} setValues={setValues} onChange={onChange} />
        </div>
      )}
    </div>
  );
};

interface ITagGroupProps {
  values: string[];
  onDelete: (index: number) => void;
}

const TagGroup = ({ values, onDelete }: ITagGroupProps) => {
  const [currentActiveIndex, setCurrentActiveIndex] = useState<number | null>(null);

  return (
    <>
      {values.map((value, index) => (
        <Tag
          key={index}
          value={value}
          onDelete={() => {
            onDelete(index);
          }}
          isActive={currentActiveIndex === index}
          onClick={() => setCurrentActiveIndex(index)}
        />
      ))}
    </>
  );
};

interface ITagProps {
  value: string;
  onDelete: () => void;
  onClick: () => void;
  isActive: boolean;
}

const Tag = ({ value, onDelete, onClick, isActive }: ITagProps) => {
  const [anchorIsHovered, setAnchorIsHovered] = useState(false); //might be able to do with CSS, but for now, this works
  return (
    <button
      tabIndex={0}
      onClick={onClick}
      className={classNames({
        'tw-[transition:background_.1s_ease] tw-my-[.14285714rem] tw-ml-[0] tw-mr-[.28571429rem] tw-inline-block tw-cursor-pointer tw-select-none tw-rounded-[.28571429rem] tw-border-[0] tw-border-[transparent] tw-px-[.78571429em] tw-py-[.35714286em] tw-font-[Lato,_"Helvetica_Neue",_Arial,_Helvetica,_sans-serif] tw-text-[14px] tw-font-bold tw-normal-case tw-leading-none tw-shadow-semantic-ui-tag':
          true,
        "tw-bg-[#e8e8e8] tw-text-[rgba(0,_0,_0,_0.6)] hover:tw-border-[#e0e0e0] hover:tw-bg-[#e0e0e0] hover:tw-text-[rgba(0,_0,_0,_.8)]":
          !isActive,
        "tw-border-[#d0d0d0] tw-bg-[#d0d0d0] tw-text-[rgba(0,_0,_0,_.95)] hover:tw-border-[#c8c8c8] hover:tw-bg-[#c8c8c8]":
          isActive,
      })}
      onMouseEnter={() => setAnchorIsHovered(true)}
      onMouseLeave={() => setAnchorIsHovered(false)}
    >
      {value}
      <i
        tabIndex={0}
        className={classNames({
          "delete icon tw-[transition:opacity_.1s_ease] tw-float-right tw-my-[0] tw-ml-[.5em] tw-mr-[0] tw-inline-block tw-h-auto tw-w-auto tw-cursor-pointer tw-text-center tw-opacity-[.8] hover:tw-text-[rgba(0,_0,_0,_.8)]":
            true,
          "tw-text-[rgba(0,_0,_0,_0.4)]": !anchorIsHovered && !isActive,
          "tw-text-[rgba(0,_0,_0,_0.6)]": anchorIsHovered || isActive,
        })}
        role='button'
        onClick={onDelete}
      />
    </button>
  );
};

interface IMultiStringInputProps {
  inputRef: React.RefObject<HTMLInputElement>;
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>>;
  onChange?: (values: string[]) => void;
}

export const MultiStringInput = ({ inputRef, values, setValues, onChange }: IMultiStringInputProps) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div
      tabIndex={0}
      className={classNames({
        "multi-string tw-relative tw-w-full tw-cursor-text tw-overflow-hidden tw-whitespace-normal tw-rounded-[.28571429rem] tw-border-[1px] tw-border-solid tw-bg-white tw-p-0 tw-pl-2 tw-text-[1em] tw-font-normal tw-not-italic tw-text-[rgba(0,_0,_0,_.87)]":
          true,
        "tw-border-[rgba(34,36,38,.15)] hover:tw-border-[rgba(34,36,38,.3)]": !isFocused,
        "tw-border-[#85b7d9]": isFocused,
      })}
      role='button'
      onClick={() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <TagGroup
        values={values}
        onDelete={(index) => {
          const newValues = [...values];
          newValues.splice(index, 1);
          setValues(newValues);
          if (onChange) {
            const currentValue = inputRef.current?.value;
            const valuesToUse = currentValue ? [...newValues, currentValue.trim()] : newValues;
            onChange(valuesToUse);
          }
        }}
      />
      <input
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        ref={inputRef}
        className='tw-ml-2 tw-w-fit tw-cursor-text tw-border-none tw-bg-none tw-p-0 tw-py-[.67857143em] tw-align-middle tw-outline-none focus:tw-outline-none'
        type='text'
        onChange={(e) => {
          const value = e.target.value;
          if (value.endsWith(",")) {
            const newValues = [...values, value.trim().slice(0, -1)];
            setValues(newValues);
            if (inputRef.current) {
              inputRef.current.value = "";
            }
            onChange?.(newValues);
            return;
          }
          onChange?.([...values, value.trim()]);
        }}
      />
    </div>
  );
};

interface IControlledStringFilterProps extends IBaseControlProps, IStringFilterProps {}

export const ControlledStringFilter = ({ control, name, errors, ...props }: IControlledStringFilterProps) => {
  const errorMessage = nestedErrorMessageSelector(errors)(name);
  return (
    <Controller
      control={control}
      name={name}
      errors={errors}
      defaultValue={{
        jsonValue: props.defaultValues || [],
        stringCondition: props.defaultOperator || "Equal",
      }}
      render={({ onChange, value: formValue }) => (
        <>
          <StringFilter
            {...props}
            defaultValues={formValue?.jsonValue || props.defaultValues}
            defaultOperator={formValue?.stringCondition || props.defaultOperator}
            onChange={(values) => {
              onChange({
                ...formValue,
                jsonValue: values,
              });
              if (props.onChange) {
                props.onChange(values);
              }
            }}
            onOperatorChange={(operator, values) => {
              onChange({
                ...formValue,
                stringCondition: operator,
                jsonValue: values,
              });
              if (props.onOperatorChange) {
                props.onOperatorChange(operator, values);
              }
            }}
          />
          {!!errorMessage && (
            <SemanticLabel className='tw-ml-0' basic prompt pointing='above'>
              {errorMessage}
            </SemanticLabel>
          )}
        </>
      )}
    />
  );
};
